import { useState, useEffect, useCallback } from "react";
import { ChevronDown, ChevronRight, Package, Check, X, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useProjectStore } from "@/stores/project";
import { useTemplateLabels } from "@/hooks/useTemplateLabels";
import type { ParkedEntity } from "@/api/types";
import { apiClient } from "@/api/client";

/**
 * Collapsible "Noted Items" panel for Felix's entity parking lot.
 *
 * Shows entities that Felix parked during conversation — items the user
 * mentioned that Felix will come back to after the current discussion.
 * Users can promote (create) or dismiss each item.
 */
export function ParkedEntitiesPanel() {
  const projectId = useProjectStore((s) => s.selectedProjectId);
  // Template-layer SKIN of each noted-item's entity-type sub-label. The
  // humanized slug this panel shows today is the per-surface fallback, so a
  // non-template project renders byte-identical.
  const { label: skinLabel } = useTemplateLabels(projectId ?? null);
  const [items, setItems] = useState<ParkedEntity[]>([]);
  const [isOpen, setIsOpen] = useState(true);
  const [loading, setLoading] = useState(false);

  const fetchItems = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const res = await apiClient.get<{
        items: ParkedEntity[];
        count: number;
      }>(`/assistant/parked-entities?project_id=${projectId}`);
      setItems(res.items || []);
    } catch {
      // Silently ignore — endpoint may not exist yet
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    fetchItems();
    // Poll every 30 seconds while open
    const interval = setInterval(fetchItems, 30_000);
    return () => clearInterval(interval);
  }, [fetchItems]);

  // Refresh when parked entities change (listen for custom event)
  useEffect(() => {
    const handler = () => fetchItems();
    window.addEventListener("parked-entities-changed", handler);
    return () => window.removeEventListener("parked-entities-changed", handler);
  }, [fetchItems]);

  if (items.length === 0) return null;

  const entityTypeLabel = (type: string) =>
    skinLabel(type, {
      fallback: type.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
    });

  return (
    <div className="shrink-0 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center gap-2 px-3 py-2 text-[11px] font-medium text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-slate-100 transition-colors"
      >
        {isOpen ? (
          <ChevronDown className="h-3 w-3" />
        ) : (
          <ChevronRight className="h-3 w-3" />
        )}
        <Package className="h-3.5 w-3.5 text-amber-500" />
        <span>
          Noted Items ({items.length})
        </span>
        {loading && <Loader2 className="h-3 w-3 animate-spin ml-auto" />}
      </button>

      {isOpen && (
        <div className="px-3 pb-2 space-y-1.5 max-h-[160px] overflow-y-auto">
          {items.map((item) => (
            <div
              key={item.id}
              className="flex items-start gap-2 p-2 rounded-md bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-[11px]"
            >
              <div className="flex-1 min-w-0">
                <div className="font-medium text-slate-900 dark:text-slate-100 truncate">
                  {item.title}
                </div>
                <div className="text-slate-500 dark:text-slate-400 mt-0.5">
                  {entityTypeLabel(item.entity_type)}
                  {item.description && (
                    <span className="ml-1">— {item.description.slice(0, 60)}{item.description.length > 60 ? "…" : ""}</span>
                  )}
                </div>
              </div>
              <div className="flex gap-1 shrink-0">
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-5 w-5 p-0 text-green-600 hover:text-green-700 hover:bg-green-50 dark:text-green-400 dark:hover:bg-green-900/20"
                  title="Create this entity"
                  onClick={() => {
                    // Dispatch event for Felix to promote
                    window.dispatchEvent(
                      new CustomEvent("felix-promote-parked", {
                        detail: { parkedEntityId: item.id, title: item.title, entityType: item.entity_type },
                      })
                    );
                  }}
                >
                  <Check className="h-3 w-3" />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-5 w-5 p-0 text-slate-500 dark:text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                  title="Dismiss"
                  onClick={() => {
                    window.dispatchEvent(
                      new CustomEvent("felix-dismiss-parked", {
                        detail: { parkedEntityId: item.id, title: item.title },
                      })
                    );
                  }}
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
